import { DocumentSnapshot } from 'firebase/firestore';
import { type Wallpaper, DeviceType } from '../schema';
import { wallpaperQueryService, type WallpaperQuery } from './wallpaperQueryService';
import { wallpaperPackService } from './wallpaperPackService';
import { wallpaperAdminService, type CreateWallpaperData } from './wallpaperAdminService';
import { wallpaperValidationService } from './wallpaperValidationService';
import { wallpaperTransformService } from './wallpaperTransformService';
import { categoryService } from './categoryService';
import {
  type DeviceVariation,
  type CreateWallpaperPackData
} from '../../types/device';

// Re-export types for convenience
export type { Wallpaper } from '../schema';
export type { WallpaperQuery, CreateWallpaperData };

/**
 * Main wallpaper service that orchestrates all wallpaper-related operations
 * This service acts as a facade for the specialized services
 */
export class WallpaperService {
  // Query operations
  async getWallpapers(queryParams: WallpaperQuery = {}): Promise<{
    wallpapers: Wallpaper[];
    lastDoc: DocumentSnapshot | null;
  }> {
    return wallpaperQueryService.getWallpapers(queryParams);
  }

  async getWallpaperById(id: string): Promise<Wallpaper | null> {
    return wallpaperQueryService.getWallpaperById(id);
  }

  async searchWallpapers(searchTerm: string, limitCount = 20): Promise<Wallpaper[]> {
    return wallpaperQueryService.searchWallpapers(searchTerm, limitCount);
  }

  async getWallpapersByCategory(category: string, limitCount = 20): Promise<Wallpaper[]> {
    return wallpaperQueryService.getWallpapersByCategory(category, limitCount);
  }

  // Pack operations
  async createWallpaperPack(packData: CreateWallpaperPackData): Promise<Wallpaper> {
    return wallpaperPackService.createWallpaperPack(packData);
  }

  async updateWallpaperPack(id: string, packData: Partial<CreateWallpaperPackData>): Promise<void> {
    return wallpaperPackService.updateWallpaperPack(id, packData);
  }

  async convertWallpaperToPack(id: string, packData: CreateWallpaperPackData): Promise<void> {
    return wallpaperPackService.convertSingleToPack(id, packData);
  }

  async getWallpaperPackVariations(id: string): Promise<Map<DeviceType, DeviceVariation>> {
    return wallpaperPackService.getWallpaperPackVariations(id);
  }

  async deleteWallpaperPack(id: string): Promise<void> {
    return wallpaperPackService.deleteWallpaperPack(id);
  }

  // Admin operations
  async createWallpaper(data: CreateWallpaperData & { categoryId?: string }): Promise<Wallpaper> {
    return wallpaperAdminService.createWallpaper(data);
  }

  async updateWallpaper(id: string, data: Partial<CreateWallpaperData & { categoryId?: string }>): Promise<void> {
    return wallpaperAdminService.updateWallpaper(id, data);
  }

  async deleteWallpaper(id: string): Promise<void> {
    return wallpaperAdminService.deleteWallpaper(id);
  }

  async batchDeleteWallpapers(wallpaperIds: string[]): Promise<void> {
    return wallpaperAdminService.batchDeleteWallpapers(wallpaperIds);
  }

  async incrementDownloadCount(wallpaperId: string): Promise<void> {
    return wallpaperAdminService.incrementDownloadCount(wallpaperId);
  }

  async likeWallpaper(wallpaperId: string, userId: string): Promise<void> {
    return wallpaperAdminService.likeWallpaper(wallpaperId, userId);
  }

  async unlikeWallpaper(wallpaperId: string, userId: string): Promise<void> {
    return wallpaperAdminService.unlikeWallpaper(wallpaperId, userId);
  }

  // Legacy method - deprecated
  /**
   * @deprecated Use categoryService.getAllCategories() instead
   */
  async getCategories(): Promise<string[]> {
    try {
      const categories = await categoryService.getAllCategories();
      return categories.map(cat => cat.name);
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  }

  // Utility methods (expose transform and validation services)
  generateSearchKeywords(title: string, description?: string, category?: string, tags?: string[]): string[] {
    return wallpaperTransformService.generateSearchKeywords(title, description, category, tags);
  }

  validateWallpaperPack(packData: CreateWallpaperPackData) {
    return wallpaperValidationService.validateWallpaperPack(packData);
  }

  validateWallpaperData(data: {
    title?: string;
    category?: string;
    imageUrl?: string;
    tags?: string[];
  }) {
    return wallpaperValidationService.validateWallpaperData(data);
  }
}

export const wallpaperService = new WallpaperService();